import { Observable } from "rxjs";

import { DataStoreOptions, EntityField, SearchQuery, SearchResponse } from "@skinsoft/types";
import { offlineAuditCampaignSortEntityFields, offlineConditionReportSortEntityFields, offlineEntityFieldsForAdvancedSearches, offlineEntityFieldsForESFields, offlineItemSortEntityFields, offlineSpecimenSortEntityFields } from "@skinsoft/config";

import { EntityFieldDataStore } from "@skinsoft/entity";
import { CACHE_KEYS, EntityFieldOfflineDataFetcher, OfflineDataFetcher } from "@skinsoft/offline";
import { EntitySyncDownHandler } from "../../entity-sync-down-handler";

declare const ENTITY_FIELDS_TYPES_FOR_SYNC_DOWN: string[]

declare const config
const SEARCH_PAGE_SIZE = config.syncDownPageSize.entityField ?? config.syncDownPageSize.default

export class EntityFieldSyncDownHandler extends EntitySyncDownHandler<EntityField, EntityField, EntityField> {
    private static instance: EntityFieldSyncDownHandler = null as any

    public static getInstance() {
        if (!EntityFieldSyncDownHandler.instance) {
            EntityFieldSyncDownHandler.instance = new EntityFieldSyncDownHandler()
        }

        return EntityFieldSyncDownHandler.instance
    }

    public static install(Vue) {
        EntityFieldSyncDownHandler.getInstance()
    }

    constructor() {
        // See EntityFieldDataStore.searchAll (explains why we don't use 'entityField' for types)
        const typesForSearch = ['physicalMuseumItem', 'recolement', 'auditProject', 'conditionReport', 'thesaurusTerm', 'thesaurus', 'TranslatedString', 'specimen']
        const typesForSyncDown = ENTITY_FIELDS_TYPES_FOR_SYNC_DOWN ? ENTITY_FIELDS_TYPES_FOR_SYNC_DOWN : typesForSearch

        super(
            typesForSyncDown,
            EntityFieldDataStore.getInstance(),
            new EntityFieldOfflineDataFetcher(),
            CACHE_KEYS.EntityField,
            SEARCH_PAGE_SIZE
        )
    }

    async beforeSynchDown(): Promise<void> {
        await super.beforeSynchDown()
        await this.addOfflineOnlyEntities()
    }

    // Overriden: We cannot use 'paginated-search' to retrieve entityFields. See the comment on EntityFieldDataStore.searchAll method
    sendSynchroDownRequest(searchQuery: SearchQuery, options: DataStoreOptions): Observable<SearchResponse<EntityField>> {
        return this.dataStore.searchAll(
            searchQuery, 
            undefined,
            undefined,
            options,
        )
    }

    async saveSynchDownDataInCache(data: EntityField[]): Promise<void> {
        await super.saveSynchDownDataInCache(data)

        for(const entityField of data) {
            if(entityField.entityFields?.length) {
                await this.saveSynchDownDataInCache(entityField.entityFields)
            }
        }
    }

    getNbResultsInResponse(response: SearchResponse<EntityField>): number {
        // we make 1 request for each type defined in the constructor (typesForSyncDown)
        // if in the future we take more than 1 request by type, we will have to change 
        // the current method
        const hasResults = response.results?.length > 0 || response.allData?.length > 0

        return hasResults ? 1 : 0
    }

    private async addOfflineOnlyEntities() {
        await this.offlineDataFetcher.append([
            ...offlineItemSortEntityFields,
            ...offlineEntityFieldsForESFields,
            ...offlineConditionReportSortEntityFields,
            ...offlineEntityFieldsForAdvancedSearches,
            ...offlineAuditCampaignSortEntityFields,
            ...offlineSpecimenSortEntityFields
        ])
    }
}